// The empty state: one drawing, a kicker, a title, a lead and what to do, at three sizes. lg fills a pane
// (the idle machine over "Start something on dev1."), md fills a panel or a table, sm sits in a column as a
// soft card (a fill, no border). The drawings share one 2:1 isometric grid and one palette (.ds-art in ds.css), so they follow the
// theme, and draw at 1×, ¾ and ½ with 1px strokes at every size.

import type { HTMLAttributes, ReactNode } from 'react';

export type EmptySize = 'sm' | 'md' | 'lg';
export type EmptyArtKind = 'machine' | 'port' | 'tray' | 'box' | 'box-closed' | 'desktop' | 'list' | 'mail' | 'ticket' | 'laptop' | 'search';

const SCALE: Record<EmptySize, number> = { lg: 1, md: 0.75, sm: 0.5 };

// the chassis' six bays, one lit (top face, in its own u/v)
const BAYS = [6, 24, 42].flatMap((u) => [6, 18].map((v) => ({ u, v, lit: u === 24 && v === 18 })));

/** The machine, on: the /plans chassis with six bays on top (one running) and the live light on its front. */
const Chassis = () => (
  <>
    <polygon className="ds-art-left" points="2,18 62,48 62,60 2,30" />
    <polygon className="ds-art-right" points="62,48 94,32 94,44 62,60" />
    <polygon className="ds-art-top" points="34,2 94,32 62,48 2,18" />
    <g transform="matrix(1,0.5,-1,0.5,34,2)">
      {BAYS.map((b) => (
        <rect key={`${b.u}.${b.v}`} className={b.lit ? 'ds-art-bay ds-art-lit' : 'ds-art-bay'} x={b.u} y={b.v} width="12" height="8" rx="1.5" />
      ))}
      <circle className="ds-art-dot" cx="32" cy="22" r="1.5" />
    </g>
    <g transform="matrix(1,0.5,0,1,2,18)">
      {[6, 9, 12, 15].map((u) => (
        <path key={u} className="ds-art-vent" d={`M${u} 4V8`} />
      ))}
      <circle className="ds-art-halo" cx="52" cy="6" r="3" />
      <circle className="ds-art-led" cx="52" cy="6" r="1.5" />
    </g>
  </>
);

/** A flat slab on the grid, U along the right axis, V along the left, T thick; its top face's plane for what's drawn on it. */
function slab(U: number, V: number, T: number) {
  const ox = 2 + V;
  const at = (u: number, v: number, down = 0) => `${ox + u - v},${2 + (u + v) / 2 + down}`;
  return {
    box: [1.5, 1.5, U + V + 1, (U + V) / 2 + T + 1] as [number, number, number, number],
    plane: `matrix(1,0.5,-1,0.5,${ox},2)`,
    faces: (
      <>
        <polygon className="ds-art-left" points={[at(0, V), at(U, V), at(U, V, T), at(0, V, T)].join(' ')} />
        <polygon className="ds-art-right" points={[at(U, 0), at(U, V), at(U, V, T), at(U, 0, T)].join(' ')} />
        <polygon className="ds-art-top" points={[at(0, 0), at(U, 0), at(U, V), at(0, V)].join(' ')} />
      </>
    ),
  };
}

/** A laptop, open: the deck a slab (U×V, T thick), the lid standing on its back edge, as tall as the deck is deep (it folds onto it). */
const LAPTOP = (() => {
  const U = 54, V = 36, T = 3, H = 36;
  const ox = 2 + V, oy = 3 + H;
  const at = (u: number, v: number, z: number) => `${ox + u - v},${oy + (u + v) / 2 - z}`;
  const poly = (...p: string[]) => p.join(' ');
  return {
    box: [1.5, 1.5, U + V + 4, oy + (U + V) / 2 + T - 1] as [number, number, number, number],
    deck: `matrix(1,0.5,-1,0.5,${ox},${oy})`,
    lid: `matrix(1,0.5,0,1,${ox},${oy - H})`,
    faces: (
      <>
        <polygon className="ds-art-left" points={poly(at(0, V, 0), at(U, V, 0), at(U, V, -T), at(0, V, -T))} />
        <polygon className="ds-art-right" points={poly(at(U, 0, 0), at(U, V, 0), at(U, V, -T), at(U, 0, -T))} />
        <polygon className="ds-art-top" points={poly(at(0, 0, 0), at(U, 0, 0), at(U, V, 0), at(0, V, 0))} />
        <polygon className="ds-art-left" points={poly(at(0, 0, 0), at(U, 0, 0), at(U, 0, H), at(0, 0, H))} />
        <polygon className="ds-art-right" points={poly(at(U, -2, 0), at(U, 0, 0), at(U, 0, H), at(U, -2, H))} />
        <polygon className="ds-art-top" points={poly(at(0, -2, H), at(U, -2, H), at(U, 0, H), at(0, 0, H))} />
      </>
    ),
  };
})();

/** An empty folder (the Mac's DSEmptyArt .box, point for point): an archive box, U along the right axis, V along
 *  the left, H tall, walls t thick; two folders filed in it and a dashed one where the next lands. */
const BOX = (() => {
  const U = 56, V = 36, H = 24, t = 3;
  const at = (u: number, v: number, z: number) => `${38 + u - v},${28 + (u + v) / 2 - z}`;
  const poly = (...p: string[]) => p.join(' ');
  // a folder standing at depth v: its body from u0 to u1, h tall, a tab from ta to tb 5 above it
  const folders = [
    { v: 9, u0: 8, u1: 50, h: 34, ta: 32, tb: 44 },
    { v: 17, u0: 6, u1: 48, h: 31, ta: 19, tb: 31 },
    { v: 25, u0: 5, u1: 47, h: 28, ta: 7, tb: 19 },
  ];
  const folder = (f: (typeof folders)[number]) =>
    `M${f.u0} 0V${f.h}H${f.ta}L${f.ta + 1.5} ${f.h + 5}H${f.tb - 1.5}L${f.tb} ${f.h}H${f.u1}V0Z`;
  const plane = (v: number) => `matrix(1,0.5,0,-1,${38 - v},${28 + v / 2})`;
  return (
    <>
      {/* the rim, then the far walls inside it and its floor */}
      <polygon className="ds-art-top" points={poly(at(0, 0, H), at(U, 0, H), at(U, V, H), at(0, V, H))} />
      <polygon className="ds-art-right" points={poly(at(t, t, 0), at(U - t, t, 0), at(U - t, V - t, 0), at(t, V - t, 0))} />
      <polygon className="ds-art-left" points={poly(at(t, t, H), at(U - t, t, H), at(U - t, t, 0), at(t, t, 0))} />
      <polygon className="ds-art-right" points={poly(at(t, t, H), at(t, V - t, H), at(t, V - t, 0), at(t, t, 0))} />
      {/* the folders, back to front, each standing in its plane (u along, z up) with its tab */}
      {folders.map((f, i) => (
        <g key={i} transform={plane(f.v)}>
          {i === folders.length - 1 ? (
            <>
              {/* lit is a tint in dark: the folder behind mustn't show through */}
              <path className="ds-art-under" d={folder(f)} />
              <path className="ds-art-lit-hint" d={folder(f)} />
            </>
          ) : (
            <path className={i === 0 ? 'ds-art-top' : 'ds-art-left'} d={folder(f)} />
          )}
        </g>
      ))}
      {/* the near walls, their rim, and the hand hole */}
      <polygon className="ds-art-left" points={poly(at(0, V, H), at(U, V, H), at(U, V, 0), at(0, V, 0))} />
      <polygon className="ds-art-right" points={poly(at(U, 0, H), at(U, V, H), at(U, V, 0), at(U, 0, 0))} />
      <polygon className="ds-art-top" points={poly(at(0, V, H), at(U, V, H), at(U - t, V - t, H), at(t, V - t, H))} />
      <polygon className="ds-art-top" points={poly(at(U, 0, H), at(U, V, H), at(U - t, V - t, H), at(U - t, t, H))} />
      <g transform={plane(V)}>
        <rect className="ds-art-socket" x={U / 2 - 8} y={H - 10} width="16" height="4.5" rx="2.25" />
      </g>
    </>
  );
})();

/** The same archive box with its lid on: the files are there, shut away for now (Files can't reach them). The lid
 *  overhangs the box by o on every side and covers its top L, standing 1 above the rim; the hand hole sits on the
 *  front just under it, where a hand lifts it. No blue: nothing is going anywhere. */
const BOX_CLOSED = (() => {
  // a storage box, near a cube: its front about square (the open one is a tray of folders, this one stands)
  const U = 46, V = 36, H = 44, o = 1.5, L = 8;
  const z0 = H - L, z1 = H + 1;
  const at = (u: number, v: number, z: number) => `${41 + u - v},${48.5 + (u + v) / 2 - z}`;
  const poly = (...p: string[]) => p.join(' ');
  return (
    <>
      {/* the box below the lid: its front and its side */}
      <polygon className="ds-art-left" points={poly(at(0, V, z0), at(U, V, z0), at(U, V, 0), at(0, V, 0))} />
      <polygon className="ds-art-right" points={poly(at(U, 0, z0), at(U, V, z0), at(U, V, 0), at(U, 0, 0))} />
      <g transform={`matrix(1,0.5,0,-1,${41 - V},${48.5 + V / 2})`}>
        <rect className="ds-art-socket" x={U / 2 - 8} y="27" width="16" height="4.5" rx="2.25" />
      </g>
      {/* the lid: its top, its front and its side */}
      <polygon className="ds-art-top" points={poly(at(-o, -o, z1), at(U + o, -o, z1), at(U + o, V + o, z1), at(-o, V + o, z1))} />
      <polygon className="ds-art-left" points={poly(at(-o, V + o, z1), at(U + o, V + o, z1), at(U + o, V + o, z0), at(-o, V + o, z0))} />
      <polygon className="ds-art-right" points={poly(at(U + o, -o, z1), at(U + o, V + o, z1), at(U + o, V + o, z0), at(U + o, -o, z0))} />
    </>
  );
})();

/** The desktop, stopped: a monitor on its stand, the screen blank but for a dashed browser window where the desktop
 *  shows up (blue: what runs there). The panel W × Hs, d thick, faces you on its front (v = 20); the stand's foot a
 *  slab under it, its neck behind the panel. */
const DESKTOP = (() => {
  const ox = 22, oy = 55.5;
  const at = (u: number, v: number, z: number) => `${ox + u - v},${oy + (u + v) / 2 - z}`;
  const poly = (...p: string[]) => p.join(' ');
  const W = 84, z0 = 12, z1 = 62, v0 = 17, v1 = 20;
  return (
    <>
      {/* the foot, then the neck up to the panel */}
      <polygon className="ds-art-left" points={poly(at(30, 28, 2), at(54, 28, 2), at(54, 28, 0), at(30, 28, 0))} />
      <polygon className="ds-art-right" points={poly(at(54, 8, 2), at(54, 28, 2), at(54, 28, 0), at(54, 8, 0))} />
      <polygon className="ds-art-top" points={poly(at(30, 8, 2), at(54, 8, 2), at(54, 28, 2), at(30, 28, 2))} />
      <polygon className="ds-art-left" points={poly(at(39, 17, z0), at(45, 17, z0), at(45, 17, 2), at(39, 17, 2))} />
      <polygon className="ds-art-right" points={poly(at(45, 14, z0), at(45, 17, z0), at(45, 17, 2), at(45, 14, 2))} />
      {/* the panel: its top edge, its side, its front */}
      <polygon className="ds-art-top" points={poly(at(0, v0, z1), at(W, v0, z1), at(W, v1, z1), at(0, v1, z1))} />
      <polygon className="ds-art-right" points={poly(at(W, v0, z1), at(W, v1, z1), at(W, v1, z0), at(W, v0, z0))} />
      <polygon className="ds-art-left" points={poly(at(0, v1, z1), at(W, v1, z1), at(W, v1, z0), at(0, v1, z0))} />
      {/* on the front: the screen, and on it the browser window where the desktop appears, its title bar ruled off */}
      <g transform={`matrix(1,0.5,0,-1,${ox - v1},${oy + v1 / 2})`}>
        <rect className="ds-art-bay" x="3" y="15" width={W - 6} height="44" rx="1.5" />
        <rect className="ds-art-under" x="14" y="22" width="56" height="30" rx="1.5" />
        <rect className="ds-art-lit-hint" x="14" y="22" width="56" height="30" rx="1.5" />
        <path className="ds-art-hint" d="M14 46H70" />
      </g>
    </>
  );
})();

const LIST = slab(60, 44, 2);
const MAIL = slab(60, 40, 3);
const TICKET = slab(60, 32, 3);

const ART: Record<EmptyArtKind, { box: [number, number, number, number]; draw: ReactNode }> = {
  // the desktop, stopped: a monitor, a dashed browser window where it shows up
  desktop: { box: [1.5, 1.5, 88, 96], draw: DESKTOP },
  // can't reach the files: the archive box, its lid on
  'box-closed': { box: [1.5, 1.5, 89, 89], draw: BOX_CLOSED },
  // nothing open: the machine, idle
  machine: { box: [1.5, 1.5, 93, 59], draw: <Chassis /> },
  // nothing listening: an empty socket on its side, a plug on the floor, a dashed line where it goes
  port: {
    box: [1.5, 1.5, 114, 67],
    draw: (
      <>
        <Chassis />
        <polygon className="ds-art-socket" points="81,42.5 73,46.5 73,51.5 81,47.5" />
        <path className="ds-art-prong" d="M91 53.5 86 51M87 55.5 82 53" />
        <polygon className="ds-art-top" points="94,48 108,55 98,60 84,53" />
        <polygon className="ds-art-left" points="84,53 98,60 98,68 84,61" />
        <polygon className="ds-art-right" points="108,55 98,60 98,68 108,63" />
        <path className="ds-art-cable" d="M103 61.5C107 64 110 65 115 63.5" />
        <path className="ds-art-hint" d="M83 51C81 50 79.5 49 77.5 47.5" />
      </>
    ),
  },
  // nothing in a list: an empty tray, a dashed card where the first one lands
  tray: {
    box: [1.5, 1.5, 101, 57],
    draw: (
      <>
        <polygon className="ds-art-left" points="2,22 62,52 62,58 2,28" />
        <polygon className="ds-art-right" points="102,32 62,52 62,58 102,38" />
        <polygon className="ds-art-top" points="42,2 102,32 62,52 2,22" />
        <polygon className="ds-art-top" points="42,6 94,32 62,48 10,22" />
        {/* the far walls inside, down from the back rim to where the near rim hides them */}
        <polygon className="ds-art-left" points="42,6 94,32 88,35 42,12" />
        <polygon className="ds-art-right" points="42,6 10,22 16,25 42,12" />
        <polygon className="ds-art-hint" points="46,22 74,36 62,42 34,28" />
      </>
    ),
  },
  // an empty folder: an archive box, two folders filed in it and a dashed one where the first lands
  box: {
    box: [1.5, 1.5, 93, 74],
    draw: BOX,
  },
  // nobody waiting: a sheet of rows, the first one dashed where the next request lands
  list: {
    box: LIST.box,
    draw: (
      <>
        {LIST.faces}
        <g transform={LIST.plane}>
          <circle className="ds-art-hint" cx="9" cy="11" r="3" />
          <path className="ds-art-hint" d="M17 11H48" />
          {[
            [22, 40],
            [33, 44],
          ].map(([v, end]) => (
            <g key={v}>
              <circle className="ds-art-bay" cx="9" cy={v} r="3" />
              <path className="ds-art-vent" d={`M17 ${v}H${end}`} />
            </g>
          ))}
        </g>
      </>
    ),
  },
  // no matches: a sheet of rows, a magnifier held up over it, its glass showing a dashed, empty row
  // (nothing there). The magnifier faces you, as in an icon, not on the grid.
  search: {
    box: LIST.box,
    draw: (
      <>
        {LIST.faces}
        <g transform={LIST.plane}>
          {[
            [9, 40],
            [20, 30],
            [31, 36],
          ].map(([v, end]) => (
            <g key={v}>
              <circle className="ds-art-bay" cx="9" cy={v} r="3" />
              <path className="ds-art-vent" d={`M17 ${v}H${end}`} />
            </g>
          ))}
        </g>
        <polygon className="ds-art-left" points="77.19,35.44 87.09,45.33 91.33,41.09 81.43,31.19" />
        <circle className="ds-art-right" cx="68" cy="22" r="15" />
        <circle className="ds-art-under" cx="68" cy="22" r="12" />
        <circle className="ds-art-bay ds-art-lit" cx="68" cy="22" r="12" />
        <path className="ds-art-hint" d="M62 22H74" />
        <path className="ds-art-glint" d="M59.54 18.92A9 9 0 0 1 64.92 13.54" />
      </>
    ),
  },
  // no invites: an envelope, its seal blue
  mail: {
    box: MAIL.box,
    draw: (
      <>
        {MAIL.faces}
        <g transform={MAIL.plane}>
          <path className="ds-art-vent" d="M0 40 24 19M60 40 36 19" />
          <polygon className="ds-art-left" points="0,0 30,22 60,0" />
          <circle className="ds-art-dot" cx="30" cy="22" r="3" />
        </g>
      </>
    ),
  },
  // no devices signed in: a laptop, its screen at a prompt with the cursor blue
  laptop: {
    box: LAPTOP.box,
    draw: (
      <>
        {LAPTOP.faces}
        <g transform={LAPTOP.deck}>
          <rect className="ds-art-bay" x="5" y="4" width="44" height="15" rx="1.5" />
          <rect className="ds-art-bay" x="19" y="23" width="16" height="9" rx="1.5" />
        </g>
        <g transform={LAPTOP.lid}>
          <rect className="ds-art-bay" x="3" y="3" width="48" height="29" rx="1.5" />
          <path className="ds-art-vent" d="M8 10H16" />
          <rect className="ds-art-bay ds-art-lit" x="18" y="7.5" width="3" height="5" rx="0.5" />
        </g>
      </>
    ),
  },
  // no codes: a ticket, a perforated stub with the code in blue
  ticket: {
    box: TICKET.box,
    draw: (
      <>
        {TICKET.faces}
        <g transform={TICKET.plane}>
          <path className="ds-art-vent" d="M8 10H34M8 20H26" />
          <path className="ds-art-perf" d="M44 0V32" />
          <rect className="ds-art-bay ds-art-lit" x="49" y="11" width="6" height="10" rx="1.5" />
        </g>
      </>
    ),
  },
};

/** One of the empty-state drawings at a size's scale (lg 1×, md ¾, sm ½). */
export function EmptyArt({ kind, size = 'lg', className = '' }: { kind: EmptyArtKind; size?: EmptySize; className?: string }) {
  const { box, draw } = ART[kind];
  const k = SCALE[size];
  return (
    <svg className={`ds-art ${className}`} data-art={kind} aria-hidden="true" focusable="false" width={box[2] * k} height={box[3] * k} viewBox={box.join(' ')}>
      {draw}
    </svg>
  );
}

type EmptyProps = {
  /** The drawing; none for a plain line of text. */
  art?: EmptyArtKind;
  size?: EmptySize;
  /** A Geist Mono line over the title, after a faint "//" (lg and md). */
  kicker?: ReactNode;
  /** The headline; a plain one-line empty (the sidebar's) has only a lead. */
  title?: ReactNode;
  /** The title's element: h1 in a pane, else a heading under the page's. */
  titleAs?: 'h1' | 'h2' | 'h3' | 'p';
  lead?: ReactNode;
  /** What to do: buttons or rows, under the lead. */
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

/** The empty state: what isn't here yet, and what makes it appear. */
export function Empty({ art, size = 'md', kicker, title, titleAs, lead, children, className = '', ...rest }: EmptyProps) {
  const Title = titleAs ?? (size === 'lg' ? 'h2' : size === 'md' ? 'h3' : 'p');
  return (
    <div className={`ds-empty ${className}`} data-size={size} {...rest}>
      {art && <EmptyArt kind={art} size={size} className="ds-empty-art" />}
      {kicker && size !== 'sm' && (
        <p className="ds-empty-kicker">
          <span aria-hidden="true">//</span>
          {kicker}
        </p>
      )}
      {title && <Title className="ds-empty-title">{title}</Title>}
      {lead && <p className="ds-empty-lead">{lead}</p>}
      {children && <div className="ds-empty-do">{children}</div>}
    </div>
  );
}
